import React, { useState, useEffect } from 'react';
import axios from 'axios';
import UserModal from './UserModal';
import { MoreVertical, Edit, Trash2, Plus } from 'lucide-react';

const UserManagement = ({ isViewer }) => {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Dropdown states
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openDropdownUser, setOpenDropdownUser] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    fetchUsers();
    fetchLocations();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId &&
          !event.target.closest('.relative') &&
          !event.target.closest('.dropdown-menu')) {
        setOpenDropdownId(null);
        setOpenDropdownUser(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/users');
      setUsers(response.data);
    } catch (error) {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/locations?isActive=true');
      setLocations(response.data.locations || response.data || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  // Build location ID → number lookup
  const locationMap = {};
  locations.forEach(loc => { locationMap[loc.id] = loc.locationNumber; });

  const handleCreateUser = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleUserSaved = (message) => {
    setSuccess(message);
    setShowModal(false);
    setEditingUser(null);
    fetchUsers();
    
    // Clear success message after 3 seconds
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDeactivateUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to deactivate ${userName}?`)) {
      return;
    }

    try {
      await axios.delete(`/api/admin/users/${userId}`);
      setSuccess('User deactivated successfully');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to deactivate user');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US');
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>User Management</h2>
          {!isViewer && (
            <button
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 flex items-center gap-2"
              onClick={handleCreateUser}
            >
              <Plus className="h-4 w-4" />
              Create New User
            </button>
          )}
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        {users.length === 0 ? (
          <p>No users found.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Credit Card</th>
                  <th>Time Audit</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.firstName} {user.lastName}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`badge badge-${user.role}`}>
                        {user.role.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      {user.creditCards && user.creditCards.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {user.creditCards.map((card) => (
                            <span
                              key={card.id}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '2px 8px',
                                backgroundColor: card.UserCreditCard?.isPrimary ? '#4F46E5' : '#e5e7eb',
                                color: card.UserCreditCard?.isPrimary ? 'white' : '#374151',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '500'
                              }}
                            >
                              ••••{card.lastFourCC}
                              {card.UserCreditCard?.isPrimary && (
                                <span style={{ marginLeft: '4px', fontSize: '9px' }}>★</span>
                              )}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                          No cards
                        </span>
                      )}
                    </td>
                    <td>
                      {user.appAccess?.timeAudit ? (
                        <div>
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            backgroundColor: '#dcfce7',
                            color: '#166534',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '500',
                            marginBottom: '4px'
                          }}>
                            Enabled
                          </span>
                          {user.managedLocationIds?.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '4px' }}>
                              {user.managedLocationIds
                                .map(id => locationMap[id])
                                .filter(Boolean)
                                .sort((a, b) => parseInt(b) - parseInt(a))
                                .map(num => (
                                  <span key={num} style={{
                                    padding: '1px 6px',
                                    backgroundColor: '#e0e7ff',
                                    color: '#3730a3',
                                    borderRadius: '8px',
                                    fontSize: '10px',
                                    fontWeight: '500'
                                  }}>
                                    {num}
                                  </span>
                                ))
                              }
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>-</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${user.isActive ? 'active' : 'inactive'}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>
                      {!isViewer && (
                        <div className="relative">
                          <button
                            className="p-1 rounded-md hover:bg-gray-100 transition-colors duration-200"
                            onClick={(e) => {
                              if (openDropdownId === user.id) {
                                setOpenDropdownId(null);
                                setOpenDropdownUser(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setDropdownPosition({
                                  top: rect.bottom,
                                  left: rect.left - 180
                                });
                                setOpenDropdownId(user.id);
                                setOpenDropdownUser(user);
                              }
                            }}
                          >
                            <MoreVertical className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <UserModal
          user={editingUser}
          onClose={() => setShowModal(false)}
          onSave={handleUserSaved}
        />
      )}

      {/* Fixed positioned dropdown menu */}
      {openDropdownId && openDropdownUser && (
        <div
          className="fixed w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200 dropdown-menu"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
        >
          <div className="py-1">
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
              onClick={() => {
                handleEditUser(openDropdownUser);
                setOpenDropdownId(null);
                setOpenDropdownUser(null);
              }}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit User
            </button>
            {openDropdownUser.isActive && (
              <button
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                onClick={() => {
                  setOpenDropdownId(null);
                  setOpenDropdownUser(null);
                  handleDeactivateUser(openDropdownUser.id, `${openDropdownUser.firstName} ${openDropdownUser.lastName}`);
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Deactivate User
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;